import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connectorTypeSettingsService } from '../services/connectorTypeSettingsService';
import { connectorTypesService } from '../services/connectorTypesService';
import './ConnectorTypeSettings.css';

const ConnectorTypeSettings = () => {
  const [settings, setSettings] = useState([]);
  const [connectorType, setConnectorType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { connectorTypeId } = useParams();

  useEffect(() => {
    loadData();
  }, [connectorTypeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsResponse, typeResponse] = await Promise.all([
        connectorTypeSettingsService.list({ v2_connector_type_id: connectorTypeId }),
        connectorTypesService.get(connectorTypeId)
      ]);

      setSettings(settingsResponse.connectorTypeSettings || []);
      setConnectorType(typeResponse.connectorType);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete setting "${name}"?`)) {
      return;
    }

    try {
      await connectorTypeSettingsService.delete(id);
      await loadData();
    } catch (err) {
      console.error('Error deleting setting:', err);
      alert(`Failed to delete setting: ${err.message}`);
    }
  };

  const filteredSettings = settings.filter(setting =>
    setting.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="connector-type-settings-page">
        <div className="page-header">
          <h1>Settings</h1>
        </div>
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="connector-type-settings-page">
      <div className="page-header">
        <div>
          <button
            className="btn btn-link back-button"
            onClick={() => navigate('/admin/connector-types')}
          >
            ← Back to Connector Types
          </button>
          <h1>Settings for {connectorType?.name}</h1>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate(`/admin/connector-types/${connectorTypeId}/settings/create`)}
        >
          Create Setting
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search settings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <button
          className="btn btn-secondary"
          onClick={loadData}
        >
          Refresh
        </button>
      </div>

      <div className="settings-list">
        <table className="settings-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSettings.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? 'No settings found matching your search' : 'No settings found'}
                </td>
              </tr>
            ) : (
              filteredSettings.map(setting => (
                <tr key={setting.id}>
                  <td>{setting.id}</td>
                  <td>{setting.name}</td>
                  <td>{new Date(setting.insertdate).toLocaleString()}</td>
                  <td>{new Date(setting.updatedate).toLocaleString()}</td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => navigate(`/admin/connector-types/${connectorTypeId}/settings/${setting.id}`)}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/admin/connector-types/${connectorTypeId}/settings/${setting.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(setting.id, setting.name)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="settings-summary">
        Total: {filteredSettings.length} setting{filteredSettings.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default ConnectorTypeSettings;
